import React, {useMemo} from 'react';
import {createPortal} from 'react-dom';
import {useLocation, useNavigate} from 'react-router-dom';
import {CpuChipIcon, BeakerIcon} from '@heroicons/react/24/outline';
import ContextAnalyzer from './ContextAnalyzer';
import RelevanceEngineAB from './RelevanceEngineAB';
import AnalysisProfileSelect from './AnalysisProfileSelect';
import {useRelevanceEngineConfig} from './relevance-engine/useRelevanceEngineConfig';
import {usePortalSlot} from '../hooks/usePortalSlot';
import {AIProvider} from '../types';

export type RelevanceEngineMode = 'default' | 'classification';

const DEFAULT_MODE: RelevanceEngineMode = 'default';

function parseMode(search: string, state: unknown): RelevanceEngineMode {
    if (state && typeof state === 'object' && state !== null) {
        const m = (state as { mode?: unknown }).mode;
        // 'ab' is a legacy alias for 'classification' (old bookmarks/links).
        if (m === 'classification' || m === 'ab') return 'classification';
        if (m === 'default') return 'default';
    }
    const params = new URLSearchParams(search);
    const q = params.get('mode');
    if (q === 'classification' || q === 'ab') return 'classification';
    if (q === 'default') return 'default';
    return DEFAULT_MODE;
}

interface ModeOption {
    value: RelevanceEngineMode;
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
}

const MODE_OPTIONS: ModeOption[] = [
    {
        value: 'default',
        label: 'Default',
        description:
            'When loading URLs from the database, picks the highest-traffic pages — broad coverage of busy inventory. Each picked page is then read end-to-end by the AI and graded against your blueprint.',
        icon: CpuChipIcon,
    },
    {
        value: 'classification',
        label: 'Classification',
        description:
            'When loading URLs from the database, picks pages whose content already looks like a match for your blueprint, with a selectivity slider to tune how strict that match has to be — a topic-targeted sample. Same AI grading runs after.',
        icon: BeakerIcon,
    },
];

const PROVIDER_LABELS: Record<string, string> = {
    google: 'Google AI',
    openai: 'OpenAI',
    anthropic: 'Anthropic',
};

// Matches the chevron in AnalysisProfileSelect's trigger pill (same size,
// stroke and inset) so the native <select> pills read as the same family.
const SelectChevron: React.FC = () => (
    <svg
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-muted"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
    >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
    </svg>
);

const RelevanceEnginePage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const config = useRelevanceEngineConfig();
    // Brand Blueprint moved off this row and into AppLayout's left context
    // slot — captioned, first on the shared header row (deepcontext-tpe1).
    const contextSlot = usePortalSlot('app-page-context');

    const mode = useMemo(
        () => parseMode(location.search, location.state),
        [location.search, location.state],
    );
    const activeMode = MODE_OPTIONS.find(o => o.value === mode) ?? MODE_OPTIONS[0];

    const handleModeChange = (next: RelevanceEngineMode) => {
        if (next === mode) return;
        const params = new URLSearchParams(location.search);
        if (next === DEFAULT_MODE) {
            params.delete('mode');
        } else {
            params.set('mode', next);
        }
        const query = params.toString();
        navigate(
            { pathname: location.pathname, search: query ? `?${query}` : '' },
            { replace: true, state: { mode: next } },
        );
    };

    return (
        <div className="space-y-6">
            {contextSlot && createPortal(
                <div className="w-56">
                    <AnalysisProfileSelect
                        value={config.selectedPromptId}
                        onChange={config.setSelectedPromptId}
                        onInvalidSelection={config.onInvalidSelection}
                        className="w-full"
                        size="md"
                        required
                        captioned
                    />
                </div>,
                contextSlot,
            )}
            {/* Config — Engine / Model / mode toggle. Brand Blueprint moved to
                the shared header row's left context slot (deepcontext-tpe1),
                which starts at the same left edge as this row. Two moves
                recreate "the same line" the field used to share with these
                pills when it was inline: ml-[236px] (blueprint's w-56 + the
                header row's gap-3) shifts this row right so it starts where
                the blueprint pill's trailing edge would be instead of
                drawing on top of it, and -mt-[66px] (was -[21px], tuned to
                the sidebar's Explore nav pill before the blueprint pill
                left) pulls it up so its pills land on the SAME bottom
                baseline as the captioned blueprint field above. items-end +
                a min-h-[42px] floor keep that baseline fixed whether or not
                Engine/Model render (non-admin has only the mode toggle on
                this row). Both offsets are hardcoded pixel values tied to
                the header's current height/gap and the field's current
                width — fragile against future changes there, same as the
                pre-existing -mt-[21px] hack. Migrating this row onto the
                shared header row properly (so the alignment is structural,
                not numeric) is out of scope here — see deepcontext-tpe1's
                "out of scope" list. */}
            <div
                data-testid="relevance-engine-config-row"
                className="-mt-[66px] ml-[236px] flex flex-wrap items-end gap-4 min-h-[42px]"
            >
                {config.isAdmin && (
                    <>
                        <div className="relative w-56">
                            <select
                                data-testid="config-select-engine"
                                value={config.selectedProvider}
                                onChange={(e) => config.setSelectedProvider(e.target.value as AIProvider)}
                                className="h-[42px] w-full appearance-none rounded-full border px-4 py-2 pr-9 text-sm font-bold text-ink bg-[color-mix(in_srgb,var(--accent)_7%,var(--surface))] border-[color-mix(in_srgb,var(--accent)_18%,var(--line))] focus:ring-accent focus:border-accent"
                            >
                                {config.providers.map((p) => (
                                    <option key={p.provider} value={p.provider} disabled={!p.available}>
                                        {PROVIDER_LABELS[p.provider] ?? p.provider}
                                        {!p.available && ' (Not Available)'}
                                    </option>
                                ))}
                            </select>
                            <SelectChevron/>
                        </div>
                        <div className="relative w-56">
                            <select
                                data-testid="config-select-model"
                                value={config.selectedModel}
                                onChange={(e) => config.setSelectedModel(e.target.value)}
                                className="h-[42px] w-full appearance-none rounded-full border px-4 py-2 pr-9 text-sm font-bold text-ink bg-[color-mix(in_srgb,var(--accent)_7%,var(--surface))] border-[color-mix(in_srgb,var(--accent)_18%,var(--line))] focus:ring-accent focus:border-accent"
                            >
                                {config.providers
                                    .find((p) => p.provider === config.selectedProvider)
                                    ?.models.map((m) => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                            </select>
                            <SelectChevron/>
                        </div>
                    </>
                )}
                <div
                    role="radiogroup"
                    aria-label="Relevance Engine mode"
                    className="flex rounded-full bg-ghost-bg p-[3px]"
                    data-testid="relevance-engine-mode-selector"
                >
                    {MODE_OPTIONS.map(opt => {
                        const Icon = opt.icon;
                        const isActive = opt.value === mode;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                role="radio"
                                aria-checked={isActive}
                                onClick={() => handleModeChange(opt.value)}
                                data-testid={`relevance-engine-mode-${opt.value}`}
                                className={
                                    'flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ' +
                                    (isActive
                                        ? 'bg-surface shadow-sm text-accent-active'
                                        : 'text-ink-muted hover:text-ink')
                                }
                            >
                                <Icon className="h-4 w-4" aria-hidden="true"/>
                                {opt.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Mode description — toggle now lives in the row above */}
            <p className="text-sm text-ink-muted">{activeMode.description}</p>

            {/* URL Input — provided by the active mode child. Re-key on mode
              so each panel's URL input / results stay isolated per mode. Its
              own 16px card, not nested inside a page-level mega-container. */}
            <div className="rounded-2xl border border-line bg-surface p-4 shadow-sm">
                {mode === 'classification' ? (
                    <div data-testid="relevance-engine-panel-classification">
                        <RelevanceEngineAB key="classification" config={config}/>
                    </div>
                ) : (
                    <div data-testid="relevance-engine-panel-default">
                        <ContextAnalyzer key="default" config={config}/>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RelevanceEnginePage;
